import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Phone, Mail, MapPin, Briefcase, User, AlertCircle,
  CheckCircle, Clock, XCircle, CreditCard, Send, DollarSign
} from 'lucide-react';
import MarkPaidDialog from './MarkPaidDialog';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Status indicator component
const StatusBadge = ({ status, label }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'Paid':
      case 'Signed':
      case 'Active':
        return { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle };
      case 'Sent':
      case 'Invitation Sent':
        return { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Send };
      case 'Draft':
        return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock };
      case 'Waived':
        return { bg: 'bg-cyan-500/20', text: 'text-cyan-400', icon: CheckCircle };
      case 'Unpaid':
      case 'Not Created':
      case 'Not Enabled':
      default:
        return { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: XCircle };
    }
  };

  const style = getStatusStyle();
  const Icon = style.icon;

  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-virgo-teal-300 text-sm">{label}</span>
      <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    </div>
  );
};

function IntakeDetailModal({ isOpen, onClose, intakeId }) {
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);

  useEffect(() => {
    if (isOpen && intakeId) {
      fetchIntakeDetail();
    }
  }, [isOpen, intakeId]);

  const fetchIntakeDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/intake/${intakeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntake(response.data);
    } catch (err) {
      console.error('Error fetching intake detail:', err);
      setError('Failed to load intake details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateUtil(dateString);
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-virgo-navy-700 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-virgo-navy-600 flex justify-between items-start sticky top-0 bg-virgo-navy-700 z-10">
          <div>
            {loading ? (
              <div className="animate-pulse">
                <div className="h-7 bg-virgo-navy-600 rounded w-48 mb-2"></div>
                <div className="h-4 bg-virgo-navy-600 rounded w-32"></div>
              </div>
            ) : intake ? (
              <>
                <h2 className="text-2xl font-bold text-white">
                  {intake.first_name} {intake.last_name}
                </h2>
                <p className="text-virgo-teal-400">{intake.id}</p>
              </>
            ) : null}
          </div>
          <button
            onClick={onClose}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-virgo-teal-300">{error}</p>
            </div>
          ) : intake ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Contact & Matter Info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Status Badge */}
                <div className="flex items-center gap-3">
                  <span
                    className="px-3 py-1 rounded-full text-white text-sm font-semibold"
                    style={{ backgroundColor: intake.status_color || '#6B7280' }}
                  >
                    {intake.status_name || 'New'}
                  </span>
                  {(intake.urgency === 'high' || intake.urgency === 'critical') && (
                    <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm font-semibold">
                      High Priority
                    </span>
                  )}
                </div>

                {/* Contact Information */}
                <div className="bg-virgo-navy-600 rounded-lg p-4">
                  <h3 className="font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {intake.primary_phone && (
                      <div className="flex items-center gap-2 text-virgo-teal-300">
                        <Phone className="w-4 h-4 text-virgo-teal-500" />
                        <span>{intake.primary_phone}</span>
                      </div>
                    )}
                    {intake.email && (
                      <div className="flex items-center gap-2 text-virgo-teal-300">
                        <Mail className="w-4 h-4 text-virgo-teal-500" />
                        <span>{intake.email}</span>
                      </div>
                    )}
                    {(intake.address_street || intake.address_city) && (
                      <div className="flex items-start gap-2 text-virgo-teal-300 md:col-span-2">
                        <MapPin className="w-4 h-4 text-virgo-teal-500 mt-0.5" />
                        <span>
                          {intake.address_street && `${intake.address_street}, `}
                          {intake.address_city && `${intake.address_city}, `}
                          {intake.address_state} {intake.address_zip}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Matter Information */}
                <div className="bg-virgo-navy-600 rounded-lg p-4">
                  <h3 className="font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Matter Information
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-virgo-teal-500">Practice Area</span>
                      <span className="text-virgo-teal-300">{intake.practice_area || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-virgo-teal-500">Source</span>
                      <span className="text-virgo-teal-300">{intake.source_name || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-virgo-teal-500">Created</span>
                      <span className="text-virgo-teal-300">{formatDate(intake.created_at)}</span>
                    </div>
                    {intake.assigned_to_name && (
                      <div className="flex justify-between">
                        <span className="text-virgo-teal-500">Assigned To</span>
                        <span className="text-virgo-teal-300">{intake.assigned_to_name}</span>
                      </div>
                    )}
                  </div>
                  {intake.matter_description && (
                    <div className="mt-4 pt-4 border-t border-virgo-navy-500">
                      <p className="text-virgo-teal-500 text-sm mb-2">Description</p>
                      <p className="text-virgo-teal-300 text-sm whitespace-pre-wrap">{intake.matter_description}</p>
                    </div>
                  )}
                </div>

                {/* Consultation Notes */}
                {intake.consultation_notes && (
                  <div className="bg-virgo-navy-600 rounded-lg p-4">
                    <h3 className="font-semibold text-virgo-gold-400 mb-3">Consultation Notes</h3>
                    <p className="text-virgo-teal-300 text-sm whitespace-pre-wrap">{intake.consultation_notes}</p>
                  </div>
                )}
              </div>

              {/* Right Column - Workflow Status */}
              <div className="space-y-6">
                {/* Workflow Status Card */}
                <div className="bg-virgo-navy-600 rounded-lg p-4">
                  <h3 className="font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Workflow Status
                  </h3>
                  {intake.workflow_status ? (
                    <div className="divide-y divide-virgo-navy-500">
                      <StatusBadge
                        label="Consultation Fee"
                        status={intake.workflow_status.consultation_fee}
                      />
                      <StatusBadge
                        label="Engagement Letter"
                        status={intake.workflow_status.engagement_letter}
                      />
                      <StatusBadge
                        label="Invoice"
                        status={intake.workflow_status.invoice}
                      />
                      <StatusBadge
                        label="Client Portal"
                        status={intake.workflow_status.client_portal}
                      />
                    </div>
                  ) : (
                    <p className="text-virgo-teal-500 text-sm">No workflow status available</p>
                  )}
                </div>

                {/* Consultation Fee Details */}
                <div className="bg-virgo-navy-600 rounded-lg p-4">
                  <h3 className="font-semibold text-virgo-gold-400 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Consultation Fee
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-virgo-teal-500">Amount</span>
                      <span className="text-virgo-teal-300 font-semibold">
                        {intake.consult_fee_waived ? 'Waived' : formatCurrency(intake.consult_fee || 15000)}
                      </span>
                    </div>
                    {intake.consult_paid && intake.consult_paid_at && (
                      <div className="flex justify-between">
                        <span className="text-virgo-teal-500">Paid On</span>
                        <span className="text-green-400">{formatDate(intake.consult_paid_at)}</span>
                      </div>
                    )}
                    {!intake.consult_paid && !intake.consult_fee_waived && (
                      <button
                        onClick={() => setShowMarkPaidDialog(true)}
                        className="w-full mt-3 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <DollarSign className="w-4 h-4" />
                        Mark Paid
                      </button>
                    )}
                  </div>
                </div>

                {/* Conversion Info */}
                {intake.converted_case_id && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <h3 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Converted to Case
                    </h3>
                    <p className="text-virgo-teal-300 text-sm">{intake.converted_case_id}</p>
                    {intake.converted_at && (
                      <p className="text-virgo-teal-500 text-xs mt-1">{formatDate(intake.converted_at)}</p>
                    )}
                  </div>
                )}

                {/* Declined Info */}
                {intake.decline_reason && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Declined
                    </h3>
                    <p className="text-virgo-teal-300 text-sm">{intake.decline_reason}</p>
                    {intake.declined_at && (
                      <p className="text-virgo-teal-500 text-xs mt-1">{formatDate(intake.declined_at)}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-virgo-navy-600 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-virgo-teal-600 text-virgo-teal-400 rounded-lg hover:bg-virgo-navy-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
      <MarkPaidDialog
        isOpen={showMarkPaidDialog}
        onClose={() => setShowMarkPaidDialog(false)}
        intake={intake}
        onSuccess={() => {
          setShowMarkPaidDialog(false);
          fetchIntakeDetail();
        }}
      />
    </div>
    );
}

export default IntakeDetailModal;
